// Le rendu d'un document ABS (contrat `terobs.abs/1`), bloc par bloc, dans trois modes :
//   · « lecture » : la page de l'étude pour l'équipe et les consultants (/etudes/:id) ;
//   · « edition » : la même page, chiffres cliquables, textes modifiables, sections masquables ;
//   · « impression » : la page que le Chromium du backend imprime en PDF (/etudes/:id/impression),
//     sans aucune commande, les sections masquées et les blocs à compléter vides en moins.
// Chaque chiffre dit sa source et son millésime ; une absence s'écrit « N/D » avec son motif ; un
// chiffre modifié à la main garde sa valeur d'origine, son auteur, sa date et sa justification.
import { createContext, useContext, useState } from 'react'
import TexteMarkdown from './TexteMarkdown'
import { dateLongue, estND, formaterCellule, formaterValeur, libelleSource, lireNombre, notesTableau, ND } from './format'
import { celluleA, decimalesDe, origineDe, traceServeur, type Adresse, type Etat } from './edition'
import type { Bloc, BlocACompleter, BlocAlerte, BlocChiffresCles, BlocTableau, BlocTexte, Cellule,
              Document, RoleTexte, Section } from './types'

export type Mode = 'lecture' | 'edition' | 'impression'

/** Les gestes de la page qui édite ; absents en lecture et à l'impression. */
export interface GestesEdition {
  ouvrirCellule: (adr: Adresse, libelle: string) => void
  modifierTexte: (id: string, contenu: string) => void
  validerTexte: (id: string) => void
  basculerSection: (id: string) => void
  /** lève une erreur lisible si la saisie est incomplète */
  completer: (id: string, donnee: { valeur: number | null; texte: string | null; unite: string | null;
                                    decimales: number }, source: string) => void
  vider: (id: string) => void
}

interface Contexte {
  mode: Mode; doc: Document; gestes?: GestesEdition; documentDeTravail: boolean
  /** le document enregistré : l'origine d'un chiffre modifié pas encore enregistré s'y lit */
  reference?: Document
}
const Ctx = createContext<Contexte | null>(null)
const useCtx = () => useContext(Ctx)!

export const TITRES_ROLE: Partial<Record<RoleTexte, string>> = {
  constats: 'Ce que disent les chiffres',
  anticiper: 'Ce qu’on peut anticiper',
  piste: 'Piste',
}
export const NIVEAUX = { alerte: 'Alerte', vigilance: 'Vigilance', piste: 'Piste' } as const
export const PASTILLE_IA = 'Brouillon rédigé par l’IA, à relire'

/** Un texte du modèle de langage qu'aucun humain n'a encore validé. */
export function brouillonIa(b: BlocTexte | BlocAlerte): boolean {
  return (b.statut ?? 'brouillon') !== 'valide' && ((b.origine ?? (b.type === 'texte' ? 'gabarit' : 'calcul')) === 'ia'
    || !!b.verification)
}

/** Un bloc à compléter resté vide. */
export const aCompleterVide = (b: Bloc): b is BlocACompleter => b.type === 'a_completer' && !b.cellule

/** Les sections qu'on lit (les masquées ne s'impriment ni ne s'affichent en lecture). */
export function sectionsVisibles(doc: Document, mode: Mode): Section[] {
  return mode === 'edition' ? doc.sections : doc.sections.filter(s => !s.masquee)
}

/** Ce que le calcul avait produit, lisible : « 18,4 % », et « (Filosofi (Insee), 2021) » quand la
    source ou le millésime ont changé depuis. */
function texteOrigine(doc: Document, c: Cellule, o: Etat): string {
  const valeur = formaterValeur(o.valeur, o.texte, { unite: o.unite, decimales: o.decimales })
  if (o.source === (c.source ?? null) && o.millesime === (c.millesime ?? null)) return valeur
  const src = [libelleSource(doc, o.source), o.millesime].filter(Boolean).join(', ')
  return src ? `${valeur} (${src})` : valeur
}

/** La trace d'un chiffre modifié : celle que le serveur a posée (origine, auteur, date, justification),
    ou, avant l'enregistrement, la justification saisie et l'origine lue dans le document enregistré. */
export function traceModification(doc: Document, c: Cellule, adr?: Adresse, reference?: Document): string | null {
  const m = c.modification
  if (!m) return null
  if (traceServeur(c)) {
    return `Valeur d'origine : ${texteOrigine(doc, c, origineDe(c))}. Modifiée par `
      + `${m.auteur_nom || 'un membre de l’équipe'} le ${dateLongue(m.le)} : ${m.justification}`
  }
  const enregistree = adr && reference ? celluleA(reference, adr) : undefined
  const origine = enregistree ? `Valeur d'origine : ${texteOrigine(doc, c, origineDe(enregistree))}. ` : ''
  return `${origine}Modification à enregistrer : ${m.justification}`
}

/** Une valeur affichée : « N/D » et son renvoi de note, la marque d'un chiffre modifié, et en
    édition un bouton qui ouvre la saisie. */
function Valeur({ c, adr, libelle, renvoi }: { c: Cellule; adr?: Adresse; libelle: string; renvoi?: number | null }) {
  const { mode, gestes, doc, reference } = useCtx()
  const nd = estND(c)
  const trace = traceModification(doc, c, adr, reference)
  const titre = nd ? `${ND} : ${c.nd_motif ?? 'motif non précisé'}` : trace ?? undefined
  const contenu = (
    <>
      <span className={nd ? 'abs-nd' : undefined}>{formaterCellule(c)}</span>
      {nd && renvoi ? <sup className="abs-renvoi">({renvoi})</sup> : null}
      {c.modification ? <sup className="abs-marque-modifie" aria-label="valeur modifiée">*</sup> : null}
    </>
  )
  if (mode === 'edition' && adr && gestes) {
    return (
      <button type="button" className={`abs-valeur abs-valeur--bouton${c.modification ? ' abs-modifie' : ''}`}
        title={titre} aria-label={`Modifier la valeur : ${libelle} (${formaterCellule(c)})`}
        onClick={() => gestes.ouvrirCellule(adr, libelle)}>
        {contenu}
      </button>
    )
  }
  return <span className={`abs-valeur${c.modification ? ' abs-modifie' : ''}`} title={titre}>{contenu}</span>
}

function sourceCellule(doc: Document, c: Cellule): string | null {
  const s = libelleSource(doc, c.source)
  const millesime = c.millesime ?? (c.source ? doc.sources[c.source]?.millesime : null)
  if (!s) return millesime ? `Millésime ${millesime}` : null
  return millesime ? `${s}, ${millesime}` : s
}

function ChiffresCles({ b }: { b: BlocChiffresCles }) {
  const { doc, reference } = useCtx()
  return (
    <div className="abs-bloc">
      {b.titre && <h3 className="abs-h3">{b.titre}</h3>}
      <div className="abs-cles">
        {b.items.map(it => {
          const source = sourceCellule(doc, it.cellule)
          // La comparaison peut venir d'une autre source ou d'un autre millésime : elle le dit alors.
          const sourceComparaison = it.comparaison ? sourceCellule(doc, it.comparaison) : null
          return (
            <div key={it.id} className="abs-cle">
              <div className="abs-cle-valeur">
                <Valeur c={it.cellule} adr={{ bloc: b.id, cle: it.id, champ: 'valeur' }} libelle={it.libelle} />
              </div>
              <div className="abs-cle-libelle">{it.libelle}</div>
              {estND(it.cellule) && it.cellule.nd_motif && <div className="abs-cle-motif">{ND} : {it.cellule.nd_motif}</div>}
              {it.comparaison && (
                <div className="abs-cle-comparaison">
                  {it.comparaison_libelle ?? 'Comparaison'} :{' '}
                  <Valeur c={it.comparaison} adr={{ bloc: b.id, cle: it.id, champ: 'comparaison' }}
                    libelle={`${it.libelle}, ${it.comparaison_libelle ?? 'comparaison'}`} />
                </div>
              )}
              {(['valeur', 'comparaison'] as const).map(champ => {
                const c = champ === 'valeur' ? it.cellule : it.comparaison
                return c && c.modification ? (
                  <p key={champ} className="abs-cle-trace">
                    * {traceModification(doc, c, { bloc: b.id, cle: it.id, champ }, reference)}</p>) : null
              })}
              {source && <div className="abs-cle-source">{source}</div>}
              {sourceComparaison && sourceComparaison !== source && (
                <div className="abs-cle-source">{it.comparaison_libelle ?? 'Comparaison'} : {sourceComparaison}</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Tableau({ b }: { b: BlocTableau }) {
  const { doc, reference } = useCtx()
  const notes = notesTableau(doc, b)
  return (
    <div className="abs-bloc">
      <h3 className="abs-h3">{b.titre}</h3>
      <div className="abs-tableau-defil" tabIndex={0} role="region" aria-label={`Tableau : ${b.titre}`}>
        <table className="abs-tableau">
          <thead>
            <tr>
              <td className="abs-tableau-coin" />
              {b.colonnes.map(col => <th key={col.cle} scope="col">{col.libelle}</th>)}
            </tr>
          </thead>
          <tbody>
            {b.lignes.map(l => (
              <tr key={l.id}>
                <th scope="row">{l.libelle}</th>
                {b.colonnes.map(col => {
                  const c = l.cellules[col.cle]
                  if (!c) return <td key={col.cle} className="abs-vide" aria-label="sans objet">—</td>
                  const num = c.valeur !== null && c.valeur !== undefined
                  return (
                    <td key={col.cle} className={num ? 'abs-num' : undefined}>
                      <Valeur c={c} adr={{ bloc: b.id, cle: l.id, champ: col.cle }}
                        libelle={`${l.libelle}, ${col.libelle}`} renvoi={notes.renvoi(c)} />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="abs-notes">
        {b.note && <p>{b.note}</p>}
        {notes.motifs.map((m, i) => <p key={m}>({i + 1}) {ND} : {m}.</p>)}
        {notes.modifications.map((m, i) => (
          <p key={i} className="abs-note-modifie">
            * {m.ligne}, {m.colonne} : {traceModification(doc, m.cellule, m.adresse, reference)}.
          </p>
        ))}
        {notes.sources.length > 0 && <p className="abs-source">Source : {notes.sources.join(' ; ')}.</p>}
      </div>
    </div>
  )
}

function EditeurTexte({ id, contenu, onFermer }: { id: string; contenu: string; onFermer: () => void }) {
  const { gestes } = useCtx()
  return (
    <div className="abs-editeur">
      <label className="abs-editeur-champ">
        <span>Texte (paragraphes séparés par une ligne vide, puces « - », **gras**)</span>
        <textarea value={contenu} rows={Math.min(16, Math.max(5, contenu.split('\n').length + 2))}
          onChange={e => gestes!.modifierTexte(id, e.target.value)} />
      </label>
      <div className="abs-editeur-apercu" aria-label="Aperçu">
        <span className="abs-editeur-etiquette">Aperçu</span>
        {contenu.trim() ? <TexteMarkdown source={contenu} /> : <p className="abs-a-rediger">Texte vide.</p>}
      </div>
      <button type="button" className="abs-btn abs-btn--discret" onClick={onFermer}>Fermer l’éditeur</button>
    </div>
  )
}

function Commandes({ b }: { b: BlocTexte | BlocAlerte }) {
  const { mode, gestes } = useCtx()
  const [ouvert, setOuvert] = useState(false)
  if (mode !== 'edition' || !gestes) return null
  const origine = b.origine ?? (b.type === 'texte' ? 'gabarit' : 'calcul')
  const peutValider = (b.statut ?? 'brouillon') !== 'valide' && origine !== 'calcul'
  return (
    <>
      {b.type === 'texte' && b.consigne && <p className="abs-consigne">Consigne : {b.consigne}</p>}
      {b.verification && (
        <p className="abs-verification">
          Contrôle automatique ({b.verification.modele}) : {b.verification.nombres_verifies} nombre(s) vérifié(s)
          {(b.verification.nombres_rejetes ?? []).length > 0
            && ` ; rejeté(s) et retiré(s) du texte : ${b.verification.nombres_rejetes!.join(', ')}`}.
        </p>
      )}
      {ouvert && <EditeurTexte id={b.id} contenu={b.contenu ?? ''} onFermer={() => setOuvert(false)} />}
      <div className="abs-commandes">
        {!ouvert && <button type="button" className="abs-btn abs-btn--discret" onClick={() => setOuvert(true)}>
          Modifier le texte</button>}
        {peutValider && <button type="button" className="abs-btn" onClick={() => gestes.validerTexte(b.id)}>
          Valider ce texte</button>}
      </div>
    </>
  )
}

function Pastilles({ b }: { b: BlocTexte | BlocAlerte }) {
  const { mode, documentDeTravail } = useCtx()
  if (!brouillonIa(b) || (mode === 'impression' && !documentDeTravail)) return null
  return <span className="abs-pastille-ia">{PASTILLE_IA}</span>
}

function Texte({ b }: { b: BlocTexte }) {
  const { mode } = useCtx()
  const contenu = b.contenu ?? ''
  if (mode === 'impression' && !contenu.trim()) return null
  const titre = b.titre ?? TITRES_ROLE[b.role] ?? null
  const encadre = b.role === 'constats' || b.role === 'anticiper' || b.role === 'piste'
  return (
    <div className={`abs-bloc abs-texte abs-texte--${b.role}${encadre ? ' abs-encadre' : ''}`} id={`bloc-${b.id}`}>
      {(titre || brouillonIa(b)) && (
        <div className="abs-texte-tete">
          {titre && <h3 className={encadre ? 'abs-encadre-titre' : 'abs-h3'}>{titre}</h3>}
          <Pastilles b={b} />
        </div>
      )}
      {contenu.trim() ? <TexteMarkdown source={contenu} /> : <p className="abs-a-rediger">Texte à rédiger.</p>}
      <Commandes b={b} />
    </div>
  )
}

function Alerte({ b }: { b: BlocAlerte }) {
  return (
    <aside className={`abs-bloc abs-alerte abs-alerte--${b.niveau}`} id={`bloc-${b.id}`}>
      <div className="abs-texte-tete">
        <span className="abs-alerte-niveau">{NIVEAUX[b.niveau]}</span>
        <Pastilles b={b} />
      </div>
      <TexteMarkdown source={b.contenu} />
      {b.regle && <p className="abs-regle">Règle : {b.regle}.</p>}
      <Commandes b={b} />
    </aside>
  )
}

function SaisieCcas({ b }: { b: BlocACompleter }) {
  const { gestes } = useCtx()
  const [valeur, setValeur] = useState('')
  const [unite, setUnite] = useState('')
  const [source, setSource] = useState('')
  const [erreur, setErreur] = useState('')
  const enregistrer = (e: React.FormEvent) => {
    e.preventDefault()
    const n = lireNombre(valeur)
    try {
      gestes!.completer(b.id, { valeur: n, texte: n === null ? valeur.trim() || null : null,
                                unite: unite.trim() || null, decimales: n === null ? 0 : decimalesDe(valeur) }, source)
      setErreur('')
    } catch (err) {
      setErreur((err as Error).message)
    }
  }
  return (
    <form className="abs-saisie-ccas" onSubmit={enregistrer} aria-label={`Compléter : ${b.libelle}`}>
      <label>Valeur ou texte<input value={valeur} onChange={e => setValeur(e.target.value)} /></label>
      <label>Unité<input value={unite} onChange={e => setUnite(e.target.value)} placeholder="places, €, %…" /></label>
      <label className="abs-saisie-source">D’où vient la donnée<input value={source} onChange={e => setSource(e.target.value)}
        placeholder="document, service, date" /></label>
      <button type="submit" className="abs-btn">Enregistrer la donnée</button>
      {erreur && <p className="abs-erreur" role="alert">{erreur}</p>}
    </form>
  )
}

function ACompleter({ b }: { b: BlocACompleter }) {
  const { mode, gestes } = useCtx()
  if (mode === 'impression' && !b.cellule) return null
  if (b.cellule) {
    return (
      <div className="abs-bloc abs-ccas abs-ccas--rempli" id={`bloc-${b.id}`}>
        <p><strong>{b.libelle}</strong> : <span className="abs-valeur">{formaterCellule(b.cellule)}</span></p>
        <p className="abs-source">Donnée du CCAS. Source : {b.source_libre}.</p>
        {mode === 'edition' && gestes && (
          <button type="button" className="abs-btn abs-btn--discret" onClick={() => gestes.vider(b.id)}>
            Retirer la donnée</button>
        )}
      </div>
    )
  }
  return (
    <div className="abs-bloc abs-ccas" id={`bloc-${b.id}`}>
      <p className="abs-ccas-etiquette">À compléter par le CCAS</p>
      <p><strong>{b.libelle}</strong></p>
      <p className="abs-ccas-consigne">{b.consigne}</p>
      {mode === 'edition' && gestes && <SaisieCcas b={b} />}
    </div>
  )
}

function BlocVue({ b }: { b: Bloc }) {
  switch (b.type) {
    case 'chiffres_cles': return <ChiffresCles b={b} />
    case 'tableau': return <Tableau b={b} />
    case 'texte': return <Texte b={b} />
    case 'alerte': return <Alerte b={b} />
    case 'a_completer': return <ACompleter b={b} />
  }
}

function SectionVue({ s }: { s: Section }) {
  const { mode, gestes } = useCtx()
  const blocs = s.blocs ?? []
  return (
    <section className={`abs-section${s.masquee ? ' abs-section--masquee' : ''}`} id={s.id}
      aria-labelledby={`titre-${s.id}`}>
      <header className="abs-section-tete">
        <h2 id={`titre-${s.id}`} className="abs-h2">{s.titre}</h2>
        {mode === 'edition' && gestes && (
          <button type="button" className="abs-btn abs-btn--discret" onClick={() => gestes.basculerSection(s.id)}>
            {s.masquee ? 'Afficher la section' : 'Masquer la section'}
          </button>
        )}
      </header>
      {s.sous_titre && <p className="abs-sous-titre">{s.sous_titre}</p>}
      {s.masquee && <p className="abs-masquee-note">Section masquée : elle n’apparaît ni en lecture ni dans le PDF.</p>}
      {blocs.length === 0 && mode !== 'impression' && <p className="abs-a-rediger">Aucun contenu pour cette partie.</p>}
      {blocs.map(b => <BlocVue key={b.id} b={b} />)}
    </section>
  )
}

export function Sommaire({ doc, mode }: { doc: Document; mode: Mode }) {
  return (
    <nav className="abs-sommaire" aria-label="Sommaire de l'étude">
      <h2 className="abs-sommaire-titre">Sommaire</h2>
      <ol>
        {sectionsVisibles(doc, mode).map(s => (
          <li key={s.id} className={s.masquee ? 'abs-sommaire-masquee' : undefined}>
            <a href={`#${s.id}`}>{s.titre}</a>{s.masquee ? ' (masquée)' : ''}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export default function DocumentAbs({ doc, mode, gestes, documentDeTravail = true, reference }: {
  doc: Document; mode: Mode; gestes?: GestesEdition; documentDeTravail?: boolean; reference?: Document
}) {
  return (
    <Ctx.Provider value={{ doc, mode, gestes, documentDeTravail, reference }}>
      <div className={`abs-document abs-document--${mode}`}>
        {sectionsVisibles(doc, mode).map(s => <SectionVue key={s.id} s={s} />)}
      </div>
    </Ctx.Provider>
  )
}
